import { useEffect, useCallback, useRef } from 'react';
import { useScrollProperties } from './useScrollProperties';
import { useNTTableContext } from '../NTTableContext';

export function useTableState(enabledProp: boolean = true) {
  const { updateScrollProperties } = useScrollProperties();
  const { redimParent, setIsResizing, setTableWidth, isExpanded, setIsExpanded, tableWidth } = useNTTableContext();
  const prevWidthRef = useRef<string>('');
  const enabled = enabledProp && !isExpanded;

  const handleResize = useCallback(
    (newWidth: number | null) => {
      if (!newWidth) return;
      setTableWidth(`${newWidth - 3}px`);
      // updateScrollProperties('x');
    },
    [setTableWidth, updateScrollProperties]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = redimParent?.getBoundingClientRect().width || 0;
      let lastWidth = startWidth;
      let lastUpdate = 0;

      // Calculate bounds for resizing
      const firstColEl = document.querySelector('.first-column') as HTMLElement | null;
      const minWidth = (firstColEl ? firstColEl.offsetWidth : 450) + 32; // keep column usable
      const wrapperEl = redimParent?.parentElement;
      const containerWidth = wrapperEl?.getBoundingClientRect().width || window.innerWidth;

      // Space we must reserve on the right for the chart/details panel
      const accountPanelVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--ks-table-account-panel-width')
        .trim();
      const accountPanelNum = parseFloat(accountPanelVar) || 450;
      const rightReserve = accountPanelNum + 32; // extra buffer to match other logic

      const maxWidth = Math.max(minWidth, containerWidth - rightReserve);

      const doDrag = (e: MouseEvent) => {
        const now = Date.now();
        const newWidthRaw = startWidth + e.clientX - startX;
        // clamp to bounds
        const newWidth = Math.min(Math.max(newWidthRaw, minWidth), maxWidth);
        const handle = redimParent?.querySelector('.resize-handle');
        if (!handle?.classList.contains('resize-dragging')) {
          handle?.classList.add('resize-dragging');
        }
        // Only update if width changed significantly and enough time passed
        if (Math.abs(newWidth - lastWidth) > 1) {
          handleResize(newWidth);
          lastWidth = newWidth;
          lastUpdate = now;
        }
      };

      const stopDrag = () => {
        setIsResizing(false);
        const handle = redimParent?.querySelector('.resize-handle');
        handle?.classList.remove('resize-dragging');
        document.removeEventListener('mousemove', doDrag);
        document.removeEventListener('mouseup', stopDrag);
        // decide expansion once drag finished
        const finalWidth = redimParent?.getBoundingClientRect().width || 0;
        if (finalWidth < minWidth && !isExpanded) {
          // save a default contract width (60%) so collapsing restores to a reasonable size
          prevWidthRef.current = '60%';
          setIsExpanded(true);
        } else if (finalWidth >= minWidth + 4 && isExpanded) {
          setIsExpanded(false);
        }
        updateScrollProperties('x');
      };

      document.addEventListener('mousemove', doDrag);
      document.addEventListener('mouseup', stopDrag);
    },
    [redimParent, setIsResizing, handleResize]
  );

  useEffect(() => {
    const existingHandle = redimParent?.querySelector('.resize-handle');

    // Remove handle if not enabled or parent not ready
    if (!enabled || !redimParent) {
      if (existingHandle) {
        existingHandle.removeEventListener('mousedown', handleMouseDown as EventListener);
        existingHandle.remove();
      }
      return;
    }

    // Create handle if it doesn't exist
    if (!existingHandle) {
      const handle = document.createElement('div');
      handle.className = 'resize-handle';
      redimParent.appendChild(handle);
      handle.addEventListener('mousedown', handleMouseDown as EventListener);
    }

    return () => {
      const handle = redimParent?.querySelector('.resize-handle');
      if (handle) {
        handle.removeEventListener('mousedown', handleMouseDown as EventListener);
        handle.remove();
      }
    };
  }, [enabled, redimParent, handleMouseDown]);

  // Snap / restore width when expanded state toggles
  useEffect(() => {
    const firstColEl = document.querySelector('.first-column') as HTMLElement | null;
    const minWidth = (firstColEl ? firstColEl.offsetWidth : 450) + 32;

    if (isExpanded) {
      if (!prevWidthRef.current) {
        prevWidthRef.current = tableWidth;
      }
      setTableWidth(`${minWidth}px`);
    } else {
      if (prevWidthRef.current) {
        setTableWidth(prevWidthRef.current);
        prevWidthRef.current = '';
      }
    }
  }, [isExpanded, tableWidth, setTableWidth]);

  return {
    handleResize,
    handleMouseDown,
  };
}
